// eslint-disable-next-line max-classes-per-file
import { AreasReponse, Block, Division, Floor, FloorGroup, HasId } from '../model/areas_response';

/**
 * エリアコレクションを管理するためのインターフェース
 */
export interface AreaCollections<T extends HasId> {
  list: T[];
  get(id?: number):T | undefined;

  isEmpty(): boolean;

  contains(id: number): boolean;

  getDefault(): number;
}

/**
 * エリアコレクションインターフェースの実装
 */
class AreaCollectionsImpl<T extends HasId> implements AreaCollections<T> {
  list: T[];

  constructor(list?: T[]) {
    this.list = list ?? [];
  }

  get(id?: number): T | undefined {
    return this.list.find((s) => s.id === id);
  }

  isEmpty(): boolean {
    return this.list.length === 0;
  }

  contains(id: number): boolean {
    return this.list.some((s) => s.id === id);
  }

  getDefault(): number {
    return this.list.length === 0 ? 0 : this.list.find((s) => s.isDefault)?.id ?? 0;
  }
}

/**
 * エリア情報管理用クラス
 */
export class AreaCollection {
  private data?: AreasReponse;

  /**
   * コンストラクタ
   * @param data
   */
  public constructor(data?: AreasReponse) {
    this.data = data;
  }

  /**
   * 街区情報の取得
   */
  public divisions(): AreaCollections<Division> {
    return new AreaCollectionsImpl(this.data);
  }

  /**
   * フロアグループ情報の取得
   * @param divisionId
   */
  public floorGroups(divisionId: number): AreaCollections<FloorGroup> {
    return new AreaCollectionsImpl<FloorGroup>(this.divisions().get(divisionId)?.floorGroups);
  }

  /**
   * フロア情報の取得
   * @param divisionId
   */
  public floors(divisionId: number): AreaCollections<Floor> {
    return new AreaCollectionsImpl<Floor>(this.divisions().get(divisionId)?.floorGroups
      .flatMap((fg) => fg.floors));
  }

  /**
   * エリア情報の取得
   * @param divisionId
   * @param floorId
   */
  public blocks(divisionId: number, floorId: number): AreaCollections<Block> {
    return new AreaCollectionsImpl<Block>(this.floors(divisionId).get(floorId)?.blocks);
  }
}
